import cn from 'classnames'
import React, { FC, KeyboardEvent, MouseEvent, ReactElement, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useNavigate } from 'react-router-dom'

import { MouseEventType } from '@const/consts'
import { createUrl } from '@utils/utils'

import { Ellipsis, LinkContent } from '@infologistics/frontend-libraries'

import styles from './MenuItem.module.scss'

import { INavItemContentData } from '../../types'
import { IMenuItemProps as IProps,  } from './types'

const MenuItem: FC<IProps> = ({ item, isActive, isOpen }) => {
  const navigate = useNavigate()
  const [currentRoute, setCurrentRoute] = useState('')
  const { t } = useTranslation(['envelopes'])

  const {
    contentData,
    badge,
    icon,
    iconProps,
    iconCollapse,
    iconCollapseProps,
    isFavoriteFilter,
    hasPage = true,
    items,
    title,
    route
  } = item
  const renderedTitle = isFavoriteFilter ? title : t(title)

  const setRoute = () => {
    setCurrentRoute(hasPage ? route : items?.[0]?.route ?? route)
  }

  useEffect(setRoute)

  const handleOpenMenuList = (evt: MouseEvent | KeyboardEvent): void => {
    const menuItemHasChildren = !!item.items

    const url = createUrl(currentRoute)

    if ((menuItemHasChildren && !hasPage) || evt.type === MouseEventType.AUXCLICK) {
      evt.preventDefault()

      navigate(url)
    }
  }

  const getLinkContent = (data?: INavItemContentData): ReactElement | undefined => {
    if (!data) return undefined

    const { content, heading } = data

    return (
      <div className='d-flex flex-column font-xs'>
      <span className={cn('d-inline-block fw-700', styles.content_heading)}>
        <Ellipsis>{heading}</Ellipsis>
      </span>
      <span className='fw-400 text-muted-700'>{content}</span>
      </div>
    )
  }

  const getFavoriteFilterContent = (): ReactElement => (
    <span className={styles.favorite_filter_content}>
      <svg
        aria-hidden='true'
        className={styles.favorite_filter_icon}
        focusable='false'
        height='16'
        viewBox='0 0 16 16'
        width='16'
      >
        <path fillRule="evenodd" clipRule="evenodd" d="M10.247 6.014a1 1 0 01-.751-.545L8.002 2.46 6.529 5.465a1 1 0 01-.755.55L2.417 6.5l2.434 2.38a1 1 0 01.286.889l-.578 3.285 2.97-1.583a1 1 0 01.94 0l2.973 1.584-.574-3.288a1 1 0 01.285-.886l2.432-2.38-3.338-.488zm3.862-.027zm-12.42.62l-.053-.36.053.36zm8.703-1.582L8.57 1.353a.636.636 0 00-1.142.003L5.631 5.025l-4.086.592a.636.636 0 00-.353 1.085l2.96 2.895-.707 4.02a.636.636 0 00.926.672L8 12.355l3.629 1.934a.636.636 0 00.926-.671l-.702-4.021 2.956-2.895a.637.637 0 00-.354-1.085l-4.063-.592z"></path>
      </svg>
      <span className={styles.favorite_filter_title}>
        <Ellipsis>{renderedTitle}</Ellipsis>
      </span>
    </span>
  )

  return (
    <NavLink
      className={() => cn(
        isActive && 'active',
        hasPage && 'page-item',
        !hasPage && 'no-page',
        !hasPage && styles.no_page
      )}
      onAuxClick={handleOpenMenuList}
      onClick={handleOpenMenuList}
      title={renderedTitle}
      to={route}
    >
      <LinkContent
        badgeProps={badge ? {
          theme: badge.theme ?? 'primary',
          value: badge.name,
        } : null}
        content={isFavoriteFilter ? getFavoriteFilterContent() : getLinkContent(contentData)}
        iconBefore={icon}
        iconBeforeProps={{ ...iconProps, classes: 'mr-2' }}
        iconCollapse={iconCollapse}
        iconCollapseProps={{
          ...iconCollapseProps,
          classes: 'ml-auto',
          rotation: isOpen ? '0' : '90',
        }}
        title={isFavoriteFilter ? undefined : renderedTitle}
      />
    </NavLink>
  )
}

export default MenuItem
